import { createSlice } from "@reduxjs/toolkit";
import ApiMcp from "../../api/mcp";

/**
 * LF360-2100 stage 1 — the project's AI call log, for Analytics → Audit
 * trail → AI calls. Pages of 30 like the other audit trail tabs; `hasMore`
 * comes from the server.
 *
 * A list belongs to one query — the open project and the filters, named by
 * `key` — and to one start of it, numbered by `generation`. Starting a query
 * empties the list and takes a new generation, even for the same project and
 * filters; a page is kept only if it was asked for in the current generation.
 * A key alone is not enough: after A → B → A the key repeats, and a slow
 * answer from the first A would land in the list of the second.
 */
export const AiCallsSlice = createSlice({
  name: "aiCalls",
  initialState: {
    key: null,
    generation: 0,
    calls: null,
    hasMore: false,
    isLoading: false,
  },
  reducers: {
    startQuery: (state, action) => ({
      key: action.payload,
      generation: state.generation + 1,
      calls: [],
      hasMore: false,
      isLoading: false,
    }),
    setLoading: (state, action) => (action.payload.generation === state.generation
      ? { ...state, isLoading: action.payload.isLoading }
      : state),
    setCalls: (state, action) => {
      if (action.payload.generation !== state.generation) return state;

      const existingIds = new Set((state.calls || []).map((call) => call.id));
      const newItems = action.payload.calls.filter((call) => !existingIds.has(call.id));
      return {
        ...state,
        calls: [...(state.calls || []), ...newItems],
        hasMore: Boolean(action.payload.hasMore),
        isLoading: false,
      };
    },
  },
});

export const { startQuery, setLoading, setCalls } = AiCallsSlice.actions;

/** One page of the current query, kept only if no new query started meanwhile. */
export const fetchAiCalls = (query) => (dispatch, getState) => {
  const { generation } = getState().aiCalls;
  dispatch(setLoading({ generation, isLoading: true }));
  return ApiMcp.getProjectCalls(query)
    .then((r) => dispatch(setCalls({ generation, calls: r.data.calls, hasMore: r.data.hasMore })))
    .catch(() => dispatch(setLoading({ generation, isLoading: false })));
};

export const selectAiCalls = (state) => state.aiCalls.calls;
export const selectHasMoreAiCalls = (state) => state.aiCalls.hasMore;
export const selectAiCallsLoading = (state) => state.aiCalls.isLoading;
export const selectAiCallsKey = (state) => state.aiCalls.key;

export default AiCallsSlice.reducer;
